/**
 * License Expired Page Component
 * Displays when the enterprise license has expired and blocks access
 */

import React from 'react';
import { LicenseService } from '../../../services/enterprise/license.service';
import environment from '../../../settings/environmentLoader';

interface LicenseExpiredPageProps {
    signOut?: () => void;
}

const LicenseExpiredPage: React.FC<LicenseExpiredPageProps> = ({ signOut }) => {
    const licenseStatus = LicenseService.getLicenseStatus();
    const formattedExpiryDate = LicenseService.getFormattedExpiryDate();

    const handleSignOut = () => {
        if (signOut) {
            signOut();
        }
    };

    return (
        <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
            <div className="row justify-content-center w-100">
                <div className="col-lg-6 col-md-8 col-sm-10">
                    <div className="card shadow-lg">
                        <div className="card-body p-5 text-center">
                            {/* Logo */}
                            <div className="mb-4">
                                <img 
                                    src={environment.branding.logoPath} 
                                    alt={environment.branding.logoAlt}
                                    className="img-fluid"
                                    style={{ maxHeight: '80px' }}
                                />
                            </div>

                            {/* Main Content */}
                            <div className="mb-4">
                                <i className="bi bi-exclamation-triangle-fill text-danger mb-3" style={{ fontSize: '4rem' }}></i>
                                <h2 className="text-danger mb-3">License Expired</h2>
                                <p className="text-muted mb-4">
                                    Your VizCon license expired on <strong>{formattedExpiryDate}</strong>.
                                </p>
                                <p className="text-muted mb-4">
                                    To continue using VizCon, please contact our support team to renew your license.
                                </p>
                            </div>

                            {/* Contact Information */}
                            <div className="mb-4">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="bi bi-envelope-fill text-primary me-2"></i>
                                            <div>
                                                <small className="text-muted d-block">Support Email</small>
                                                <a href="mailto:support@vizcon.cloud" className="text-decoration-none">
                                                    support@vizcon.cloud
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="bi bi-envelope text-primary me-2"></i>
                                            <div>
                                                <small className="text-muted d-block">General Contact</small>
                                                <a href="mailto:contact@vizcon.cloud" className="text-decoration-none">
                                                    contact@vizcon.cloud
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* License Details */}
                            {licenseStatus.expiryDate && (
                                <div className="alert alert-light mb-4">
                                    <small className="text-muted">
                                        <strong>License Information:</strong><br/>
                                        Expiry Date: {formattedExpiryDate}<br/>
                                        Status: Expired
                                    </small>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                                <a 
                                    href={`mailto:support@vizcon.cloud?subject=License Renewal Request&body=Hello, I need to renew my VizCon enterprise license. My license expired on ${formattedExpiryDate}. Please provide renewal instructions.`}
                                    className="btn btn-primary"
                                >
                                    <i className="bi bi-envelope me-2"></i>
                                    Contact Support
                                </a>
                                {signOut && (
                                    <button 
                                        onClick={handleSignOut}
                                        className="btn btn-outline-secondary"
                                    >
                                        <i className="bi bi-box-arrow-right me-2"></i>
                                        Sign Out
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LicenseExpiredPage;